import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import { Field, FieldSet } from '../../components/Form';
import Tooltip from '../../components/Tooltip';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import copyIcon from '../../icons/non-prefilled/16x16/copy.svg';
import type { IntegrationPlatformInfo } from '../../integrations/IntegrationPlatformInfo';
import { UIActions } from '../../ui/uiSlice';
import * as styles from './SCIMTab.module.scss';

const REGENERATE_SCIM_TOKEN_MUTATION = gql(/** @complexity 4 */ `
  mutation regenerateScimToken($platform: String!) {
    regenerateScimToken(platform: $platform) {
      id
      settings {
        scimToken
      }
    }
  }
`);

const INTEGRATION_FRAGMENT = gql(`
  fragment SCIMTab_integration on Integration {
    id
    platform
    settings {
      enableScim
      scimToken
    }
  }
`);

type IntegrationFragment = DocumentType<typeof INTEGRATION_FRAGMENT>;

interface SCIMTabProps {
  integration: IntegrationFragment;
  platformInfo: IntegrationPlatformInfo;
}

const SCIMTab = ({
  integration: { platform, settings },
  platformInfo,
}: SCIMTabProps) => {
  const dispatch = useAppDispatch();
  const [regenerateScimToken] = useMutation(REGENERATE_SCIM_TOKEN_MUTATION, {
    variables: { platform },
  });

  const onRegenerateScimToken = () => {
    const tabUrl = document.location.toString();

    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Generate a new SCIM Token?',
        description: `This will cause the old token to stop working immediately, and the new token must be added in your ${platformInfo.name} SCIM settings before SCIM starts working again.`,
        confirmButton: {
          text: 'Regenerate Token',
          onClick: async () => {
            await regenerateScimToken();
            document.location = tabUrl;
          },
        },
      }),
    );
  };

  // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
  const CopyToken = () => {
    const [hasCopied, setCopied] = useState(false);
    const tooltipText = hasCopied ? 'Copied!' : 'Copy token';

    useEffect(() => {
      if (hasCopied) {
        const timeout = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timeout);
      }

      return undefined;
    }, [hasCopied]);

    const onClick = async () => {
      if (!settings?.scimToken) {
        return;
      }
      await navigator.clipboard.writeText(settings.scimToken);
      setCopied(true);
    };

    return (
      <Tooltip content={tooltipText}>
        <Button icon={copyIcon} onClick={onClick} aria-label="Copy" />
      </Tooltip>
    );
  };

  return (
    <FieldSet>
      <Field
        name="enableScim"
        type="checkbox"
        label="Enable SCIM Provisioning"
      />

      <Field
        name="scimToken"
        label="SCIM API Token"
        placeholder="Enable SCIM to generate token"
        append={settings?.scimToken && <CopyToken />}
        className={styles.scimToken}
        disabled
      />

      {settings?.scimToken && (
        <Button
          variant="link"
          className={styles.regenerateToken}
          onClick={onRegenerateScimToken}
        >
          Regenerate Token
        </Button>
      )}
    </FieldSet>
  );
};

SCIMTab.getFormValues = ({ settings }: IntegrationFragment) => ({
  enableScim: settings?.enableScim || false,
  scimToken: settings?.scimToken,
});

SCIMTab.getNewSettings = (
  values: ReturnType<typeof SCIMTab.getFormValues>,
) => ({ enableScim: values.enableScim });

export default SCIMTab;
